import { useEffect, useState } from 'react';
import { Box, Typography, Fade, Grow, Slide } from '@mui/material';
import Typist from 'react-typist';

const LoadingScreen = () => {
    const [showContent, setShowContent] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        // Activar animaciones de entrada
        setShowContent(true);
        // Simular progreso de carga
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(progressInterval);
                    return 100;
                }
                return prev + Math.random() * 15;
            });
        }, 200);

        // Redireccionar después de 4 segundos
        const redirectTimer = setTimeout(() => {
            window.location.replace('/');
        }, 4000);

        return () => {
            clearInterval(progressInterval);
            clearTimeout(redirectTimer);
        };
    }, []);

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #0364FF 0%, #0247B5 50%, #1a237e 100%)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100vw',
                height: '100vh',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
                        radial-gradient(circle at 40% 60%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
                    `,
                    animation: 'float 8s ease-in-out infinite',
                },
                '@keyframes float': {
                    '0%, 100%': { 
                        transform: 'translate(0, 0) scale(1)',
                        opacity: 0.7 
                    },
                    '33%': { 
                        transform: 'translate(-20px, -10px) scale(1.1)',
                        opacity: 0.9 
                    },
                    '66%': { 
                        transform: 'translate(20px, -15px) scale(0.9)',
                        opacity: 0.8 
                    }
                }
            }}
        >
            {/* Spinner Principal */}
            <Grow in={showContent} timeout={800}>
                <Box
                    sx={{
                        position: 'relative',
                        mb: { xs: 4, sm: 6 }
                    }}
                >
                    {/* Spinner Exterior */}
                    <Box
                        sx={{
                            width: { xs: 100, sm: 120, md: 140 },
                            height: { xs: 100, sm: 120, md: 140 },
                            border: '4px solid transparent',
                            borderTop: '4px solid rgba(255, 255, 255, 0.8)',
                            borderRight: '4px solid rgba(255, 255, 255, 0.4)',
                            borderRadius: '50%',
                            animation: 'spin 2s linear infinite',
                            position: 'relative',
                            filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))',
                            '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                            }
                        }}
                    >
                        {/* Spinner Interior */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: { xs: 15, sm: 20, md: 25 },
                                left: { xs: 15, sm: 20, md: 25 },
                                right: { xs: 15, sm: 20, md: 25 },
                                bottom: { xs: 15, sm: 20, md: 25 },
                                border: '3px solid transparent',
                                borderBottom: '3px solid rgba(255, 255, 255, 0.9)',
                                borderLeft: '3px solid rgba(255, 255, 255, 0.6)',
                                borderRadius: '50%',
                                animation: 'spinReverse 1.5s linear infinite',
                                '@keyframes spinReverse': {
                                    '0%': { transform: 'rotate(360deg)' },
                                    '100%': { transform: 'rotate(0deg)' }
                                }
                            }}
                        >
                            {/* Punto Central */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: { xs: 8, sm: 10, md: 12 },
                                    height: { xs: 8, sm: 10, md: 12 },
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { 
                                            transform: 'translate(-50%, -50%) scale(1)',
                                            opacity: 1 
                                        },
                                        '50%': { 
                                            transform: 'translate(-50%, -50%) scale(1.5)',
                                            opacity: 0.7 
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Partículas flotantes */}
                    {[...Array(6)].map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                position: 'absolute',
                                width: { xs: 4, sm: 6 },
                                height: { xs: 4, sm: 6 },
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '50%',
                                top: `${20 + Math.random() * 60}%`,
                                left: `${20 + Math.random() * 60}%`,
                                animation: `particle${index} ${3 + Math.random() * 2}s ease-in-out infinite`,
                                '@keyframes particle0': {
                                    '0%, 100%': { transform: 'translate(0, 0)', opacity: 0.6 },
                                    '50%': { transform: 'translate(20px, -20px)', opacity: 1 }
                                },
                                '@keyframes particle1': {
                                    '0%, 100%': { transform: 'translate(0, 0)', opacity: 0.4 },
                                    '50%': { transform: 'translate(-15px, 25px)', opacity: 0.8 }
                                },
                                '@keyframes particle2': {
                                    '0%, 100%': { transform: 'translate(0, 0)', opacity: 0.7 },
                                    '50%': { transform: 'translate(25px, 15px)', opacity: 0.9 }
                                },
                                '@keyframes particle3': {
                                    '0%, 100%': { transform: 'translate(0, 0)', opacity: 0.5 },
                                    '50%': { transform: 'translate(-20px, -15px)', opacity: 1 }
                                },
                                '@keyframes particle4': {
                                    '0%, 100%': { transform: 'translate(0, 0)', opacity: 0.6 },
                                    '50%': { transform: 'translate(10px, -25px)', opacity: 0.8 }
                                },
                                '@keyframes particle5': {
                                    '0%, 100%': { transform: 'translate(0, 0)', opacity: 0.4 },
                                    '50%': { transform: 'translate(-25px, 10px)', opacity: 0.9 }
                                }
                            }}
                        />
                    ))}
                </Box>
            </Grow>

            {/* Texto de Carga */}
            <Slide direction="up" in={showContent} timeout={1200}>
                <Box
                    sx={{
                        textAlign: 'center',
                        mb: { xs: 3, sm: 4 }
                    }}
                >
                    <Typist cursor={{ hideWhenDone: true, hideWhenDoneDelay: 500 }}>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: { xs: 24, sm: 30, md: 36 },
                                fontWeight: 600,
                                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                                letterSpacing: '1px',
                                background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            Cargando...
                        </Typography>
                    </Typist>
                </Box>
            </Slide>

            {/* Barra de Progreso */}
            <Fade in={showContent} timeout={1500}>
                <Box
                    sx={{
                        width: { xs: '80%', sm: '60%', md: '40%' },
                        maxWidth: 400,
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <Box
                            sx={{
                                width: `${Math.min(progress, 100)}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
                                borderRadius: 2,
                                transition: 'width 0.3s ease',
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                                    animation: 'shimmer 2s infinite',
                                    '@keyframes shimmer': {
                                        '0%': { transform: 'translateX(-100%)' },
                                        '100%': { transform: 'translateX(100%)' }
                                    }
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Fade>

            {/* Texto de Progreso */}
            <Fade in={showContent} timeout={1800}>
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: 14, sm: 16 },
                        fontWeight: 500,
                        textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    {Math.round(Math.min(progress, 100))}%
                </Typography>
            </Fade>
        </Box>
    );
}

export default LoadingScreen;
